/** Shared helpers for Drive side Preact pages. */

import { I18n } from 'i18n-js'
import locales from '../../locales.json'

export function createDriveI18n() {
  const i18n = new I18n(locales)
  i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'
  return i18n
}

export function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

export function jsonUrl(search = window.location.search) {
  const path = window.location.pathname.replace(/\.json$/, '')
  return `${path}.json${search || ''}`
}

export async function fetchJson(url) {
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin'
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const err = new Error(body.error || `HTTP ${res.status}`)
    err.status = res.status
    err.body = body
    throw err
  }
  return res.json()
}

export async function postJson(url, body = {}) {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken()
    },
    credentials: 'same-origin',
    body: JSON.stringify(body)
  })
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}))
    const err = new Error(payload.error || `HTTP ${res.status}`)
    err.status = res.status
    err.body = payload
    throw err
  }
  if (res.status === 204) return null
  return res.json().catch(() => null)
}

export async function deleteJson(url) {
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken()
    },
    credentials: 'same-origin'
  })
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}))
    const err = new Error(payload.error || `HTTP ${res.status}`)
    err.status = res.status
    err.body = payload
    throw err
  }
  if (res.status === 204) return null
  return res.json().catch(() => null)
}

export function formatBytes(bytes) {
  if (bytes == null) return '—'
  const n = Number(bytes)
  if (Number.isNaN(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

export function formatDate(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return String(iso)
  }
}

export function timeAgo(iso, agoLabel = '') {
  if (!iso) return null
  try {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return `0m ${agoLabel}`.trim()
    if (mins < 60) return `${mins}m ${agoLabel}`.trim()
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ${agoLabel}`.trim()
    const days = Math.floor(hours / 24)
    return `${days}d ${agoLabel}`.trim()
  } catch {
    return formatDate(iso)
  }
}

export function lightboxProps(lightbox = {}) {
  const attrs = {}
  Object.entries(lightbox).forEach(([key, value]) => {
    if (value == null || value === '') return
    attrs[key] = value
  })
  return attrs
}

export function applyQuery(params) {
  const url = new URL(window.location.href)
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') url.searchParams.delete(key)
    else url.searchParams.set(key, String(value))
  })
  return url
}
